import type { WeddingSettings } from './settings'

export type { WeddingSettings }

// ---------------------------------------------------------------------------
// Uniones de estado. Los mismos literales que guarda D1.
// ---------------------------------------------------------------------------

export type CeremonyType = 'cristiana' | 'catolica' | 'civil'
export type Plan = 'free' | 'premium'
export type WeddingStatus = 'draft' | 'published'
export type MemberRole = 'owner' | 'editor'

export type GuestSide = 'novia' | 'novio' | 'amigos' | 'trabajo' | 'iglesia' | 'otros'
export type GuestStatus = 'borrador' | 'invitado' | 'confirmado' | 'declinado' | 'sin_respuesta'

export type ModerationStatus = 'pendiente' | 'aprobado' | 'rechazado'
export type ChecklistSection = 'espiritual' | 'legal' | 'logistica'

export type EmailKind =
  | 'save_the_date'
  | 'invitacion'
  | 'recordatorio_sin_respuesta'
  | 'recordatorio_final'
  | 'agradecimiento'

/** 'pendiente' es la bandeja de salida: encolado, todavia sin enviar. */
export type EmailStatus = 'pendiente' | 'enviado' | 'simulado' | 'error'
export type PaymentStatus = 'pendiente' | 'aprobado' | 'rechazado' | 'cancelado'

export const CEREMONY_TYPES: readonly CeremonyType[] = ['cristiana', 'catolica', 'civil']
export const GUEST_SIDES: readonly GuestSide[] = [
  'novia',
  'novio',
  'amigos',
  'trabajo',
  'iglesia',
  'otros',
]
export const GUEST_STATUSES: readonly GuestStatus[] = [
  'borrador',
  'invitado',
  'confirmado',
  'declinado',
  'sin_respuesta',
]
export const CHECKLIST_SECTIONS: readonly ChecklistSection[] = ['espiritual', 'legal', 'logistica']
export const EMAIL_KINDS: readonly EmailKind[] = [
  'save_the_date',
  'invitacion',
  'recordatorio_sin_respuesta',
  'recordatorio_final',
  'agradecimiento',
]

/** Etiquetas en español para la UI. */
export const GUEST_SIDE_LABELS: Record<GuestSide, string> = {
  novia: 'Familia de la novia',
  novio: 'Familia del novio',
  amigos: 'Amigos',
  trabajo: 'Trabajo',
  iglesia: 'Iglesia',
  otros: 'Otros',
}

export const GUEST_STATUS_LABELS: Record<GuestStatus, string> = {
  borrador: 'Borrador',
  invitado: 'Invitado',
  confirmado: 'Confirmado',
  declinado: 'No asiste',
  sin_respuesta: 'Sin respuesta',
}

export const EMAIL_KIND_LABELS: Record<EmailKind, string> = {
  save_the_date: 'Reserva la fecha',
  invitacion: 'Invitación formal',
  recordatorio_sin_respuesta: 'Recordatorio a quienes no han respondido',
  recordatorio_final: 'Recordatorio final con logística',
  agradecimiento: 'Agradecimiento post-boda',
}

export const CEREMONY_TYPE_LABELS: Record<CeremonyType, string> = {
  cristiana: 'Cristiana',
  catolica: 'Católica',
  civil: 'Civil',
}

// ---------------------------------------------------------------------------
// DTOs devueltos por la API (camelCase; las filas de D1 son snake_case).
// ---------------------------------------------------------------------------

export interface User {
  id: string
  email: string
  name: string
  isAdmin: boolean
  createdAt: string
}

export interface Wedding {
  id: string
  ownerId: string
  slug: string
  partner1Name: string
  partner2Name: string
  weddingDate: string | null
  ceremonyType: CeremonyType
  plan: Plan
  status: WeddingStatus
  settings: WeddingSettings
  createdAt: string
  updatedAt: string
}

export interface WeddingSummary {
  id: string
  slug: string
  partner1Name: string
  partner2Name: string
  weddingDate: string | null
  plan: Plan
  status: WeddingStatus
  role: MemberRole
  guestCount: number
  confirmedCount: number
}

export interface Guest {
  id: string
  weddingId: string
  fullName: string
  email: string | null
  phone: string | null
  code: string
  side: GuestSide
  status: GuestStatus
  maxCompanions: number
  confirmedCompanions: number
  dietaryNotes: string | null
  notes: string | null
  respondedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface GuestCounts {
  total: number
  personas: number
  borrador: number
  invitado: number
  confirmado: number
  declinado: number
  sin_respuesta: number
}

export interface Message {
  id: string
  weddingId: string
  guestId: string | null
  authorName: string
  body: string
  moderationStatus: ModerationStatus
  createdAt: string
}

/**
 * Para qué es la foto. `propuesta` comparte tabla, cuota y ruta `/f/` con la
 * galería, pero no aparece en ninguna lista: ni en la página pública ni en la
 * rejilla del panel.
 */
export type PhotoKind = 'galeria' | 'propuesta'

export interface Photo {
  id: string
  weddingId: string
  guestId: string | null
  /** Ruta servida por el Worker desde R2: `/f/<weddingId>/<photoId>` */
  url: string
  uploaderName: string | null
  contentType: string
  sizeBytes: number
  caption: string | null
  moderationStatus: ModerationStatus
  kind: PhotoKind
  /** Fecha en que la pareja la destacó, o null. Las destacadas encabezan la galería. */
  featuredAt: string | null
  createdAt: string
}

export interface ChecklistItem {
  id: string
  weddingId: string
  templateKey: string | null
  section: ChecklistSection
  title: string
  detail: string | null
  done: boolean
  dueDate: string | null
  position: number
  completedAt: string | null
  /** Lo que la pareja resolvió/decidió sobre este punto: su memoria escrita. */
  resolution: string | null
}

export interface ChecklistProgress {
  section: ChecklistSection
  done: number
  total: number
}

export interface EmailTemplate {
  id: string
  weddingId: string
  kind: EmailKind
  subject: string
  body: string
  enabled: boolean
  /** Días ANTES de la boda. Negativo = después. null = solo envío manual. */
  offsetDays: number | null
  updatedAt: string
}

export interface EmailLogEntry {
  id: string
  weddingId: string
  guestId: string
  kind: EmailKind
  toEmail: string
  status: EmailStatus
  error: string | null
  sentAt: string
}

export interface Payment {
  id: string
  weddingId: string
  amountCents: number
  currency: string
  status: PaymentStatus
  provider: string
  clientTransactionId: string
  providerTransactionId: string | null
  createdAt: string
  updatedAt: string
}

export interface WeddingMember {
  userId: string
  email: string
  name: string
  role: MemberRole
  createdAt: string
}

/** Lo que ve un invitado. Nunca incluye datos de otros invitados. */
export interface PublicWedding {
  id: string
  slug: string
  partner1Name: string
  partner2Name: string
  weddingDate: string | null
  ceremonyType: CeremonyType
  settings: WeddingSettings
  /** true en plan gratis: la pagina muestra la marca de la plataforma. */
  branding: boolean
}

/** Una semana del pulso de confirmaciones: cuántos respondieron esa semana. */
export interface RsvpPulseWeek {
  /** Lunes de esa semana, como fecha ISO (YYYY-MM-DD). */
  weekStart: string
  confirmados: number
  declinados: number
}

export interface DashboardStats {
  guests: GuestCounts
  photos: { total: number; pendientes: number; aprobadas: number }
  messages: { total: number; pendientes: number; aprobados: number }
  checklist: { done: number; total: number; porSeccion: ChecklistProgress[] }
  diasRestantes: number | null
  /** Solo semanas con al menos una respuesta, en orden cronológico. */
  rsvpPulse: RsvpPulseWeek[]
}

/** El pase digital de un invitado confirmado. Solo datos suyos, nada más. */
export interface GuestPass {
  id: string
  fullName: string
  confirmedCompanions: number
  respondedAt: string | null
}

export interface PlanUsage {
  plan: Plan
  priceCents: number
  limits: { maxGuests: number; maxPhotos: number; maxWeddings: number; branding: boolean }
  usage: { guests: number; photos: number }
}

export interface AdminSummary {
  users: number
  weddings: number
  free: number
  premium: number
  guests: number
  photos: number
  revenueCents: number
}

export interface AdminWeddingRow {
  id: string
  slug: string
  partner1Name: string
  partner2Name: string
  weddingDate: string | null
  plan: Plan
  status: WeddingStatus
  ownerEmail: string
  guestCount: number
  photoCount: number
  createdAt: string
}

// ---------------------------------------------------------------------------
// Presupuesto y proveedores
// ---------------------------------------------------------------------------

export interface BudgetCategory {
  id: string
  weddingId: string
  /** Clave estable de la plantilla, o null si la creó la pareja. */
  templateKey: string | null
  name: string
  estimatedCents: number
  /** Suma de los pagos registrados al proveedor elegido de esta categoría. */
  paidCents: number
  /** Costo del proveedor elegido, o 0 si todavía no hay ninguno. */
  actualCents: number
  position: number
  notes: string | null
  /** Quién cubre esta categoría (novios, familia, padrinos…). null = por definir. */
  payer: string | null
}

/**
 * Tipos de integrante del cortejo. Viven AQUÍ y no en settings.ts a propósito:
 * la página pública los pinta, y settings.ts arrastra zod al bundle del
 * invitado (la misma trampa documentada del sobre digital).
 */
export const PARTY_KINDS = [
  'padrinos',
  'damas',
  'caballeros',
  'pajes',
  'testigos',
  'otros',
] as const

export type PartyKind = (typeof PARTY_KINDS)[number]

export const PARTY_KIND_LABELS: Record<PartyKind, string> = {
  padrinos: 'Padrinos y madrinas',
  damas: 'Damas de honor',
  caballeros: 'Caballeros de honor',
  pajes: 'Pajes y niños del cortejo',
  testigos: 'Testigos',
  otros: 'Acompañantes especiales',
}

/** Un sobre digital anunciado por un invitado. El comprobante es privado. */
export interface EnvelopeGift {
  id: string
  weddingId: string
  guestName: string
  message: string | null
  method: 'efectivo' | 'deuna' | 'transferencia'
  hasReceipt: boolean
  createdAt: string
}

export interface Vendor {
  id: string
  weddingId: string
  categoryId: string
  name: string
  costCents: number
  includes: string | null
  contact: string | null
  location: string | null
  notes: string | null
  /** 0 = sin calificar, 1..5 estrellas. */
  rating: number
  chosen: boolean
  createdAt: string
  updatedAt: string
}

export interface VendorPayment {
  id: string
  weddingId: string
  vendorId: string
  concept: string
  amountCents: number
  dueDate: string | null
  paidAt: string | null
  createdAt: string
  updatedAt: string
}

export interface BudgetSummary {
  totalCents: number
  estimatedCents: number
  /** Suma de los proveedores elegidos: lo que ya está comprometido. */
  committedCents: number
  /** Suma de los pagos con fecha de pago registrada. */
  paidCents: number
  /** totalCents - paidCents. Puede ser negativo si se pasaron. */
  availableCents: number
  /** Porcentaje del total ya pagado, 0..100+. */
  usedPercent: number
}

export interface CostPerGuest {
  /** Confirmados más sus acompañantes: el número que pide el catering. */
  personas: number
  /** Costo del proveedor de catering elegido, o el estimado si no hay ninguno. */
  cateringCents: number
  /** cateringCents / personas, o null si todavía no hay confirmados. */
  perPersonCents: number | null
  /** true si el monto viene del estimado y no de un proveedor elegido. */
  esEstimado: boolean
}

export interface BudgetView {
  summary: BudgetSummary
  categories: BudgetCategory[]
  vendors: Vendor[]
  payments: VendorPayment[]
  costPerGuest: CostPerGuest
}

/** Forma de todo error de la API. */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown }
}
